import { IconFileImage } from '@doltech/icons-from-dev/IconFileImage';
import { IconUploadFile } from '@doltech/icons-from-dev/IconUploadFile';
import { DATE_FORMAT_2 } from '@doltech/utils/lib/constants';
import { Upload } from 'antd';
import { RcFile, UploadProps } from 'antd/lib/upload';
import moment from 'moment';
import * as React from 'react';
import styled from 'styled-components';

import { toScreen } from '@doltech/core/lib/responsive/responsive.util';
import { Button } from '../Button';
import { figmaColors } from '../colors';
import { colorsV2 } from '../colors-v2';
import { ReadingText } from '../Typography';
import {
  ACCEPT_IMAGE_ONLY,
  handlePublicUploadWithApiCustomRequestForAntDesign,
  handleUploadWithApiCustomRequestForAntDesign,
} from './fileUploadUtils';

/* eslint-disable jsx-a11y/accessible-emoji */
export const FileSelected = styled.div`
  align-items: center;
  color: ${figmaColors.neutral2};
  flex: 1;
  text-align: left;
  display: flex;
  flex-direction: row;
  cursor: pointer;
  > div {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .left-section {
    .icon-wrapper {
      margin-right: 16px;
      width: 40px;
      height: 40px;
      font-size: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background-color: ${colorsV2.gray10};
      transition: all 0.3s ease;
    }
  }
  .content-section {
    .title {
      font-size: 16px;
      font-weight: 600;
      color: ${colorsV2.black100};
    }
    .subtitle {
      font-size: 14px;
      font-weight: 500;
      color: ${colorsV2.gray100};
      .em {
        color: ${colorsV2.blue100};
        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  &:hover {
    .icon-wrapper {
      transform: scale(1.1);
    }
  }

  ${toScreen(768)} {
    .left-section {
      display: none;
    }
  }
`;

const ButtonWrapper = styled.div`
  text-align: right;
  button {
    .reading-text {
      color: ${figmaColors.text1};
    }
  }
`;

export interface FileUploadSuccessData {
  tokenResponse: {
    deleteHeaders: any;
    deleteToken: string;
    path: string;
    resourceUrl: string;
    downloadResourceUrl: string;
    uploadHeaders: any;
    uploadToken: string;
  };
  file: {
    name: string;
    size: string;
    uploadedDate: string;
    [type: string]: string;
  };
  uploadResponse: any; // don't know
}

export interface ProgressInput {
  percent: number;
}
export interface ImageMetaData {
  alt?: string;
  caption?: string;
}
export interface FileUploadProps extends Omit<UploadProps, 'children'> {
  onProgress?: (input?: ProgressInput, file?: RcFile) => void;
  onSuccess?: (input: FileUploadSuccessData, originalFile: File) => void; // can't get type because we have custom request
  onError?: (err: any) => void; // can't get type because we have custom request
  children?: any;
  labelBtn?: string;
  fileName?: string;
  fileInfo?: any;
  fileExt?: string;
  renderActionButton?: () => void;
  required?: boolean;
  hasMetadata?: boolean;

  sizeLimitInMb?: number;
  recommendText?: any;
  uploadTitle?: string;
}
export interface ImageUploadProps extends FileUploadProps {
  onMetadataUpdate?: (metadata: ImageMetaData) => void;
  customPreview?: boolean;
}

const defaultFileUploadOptions = {
  customRequest: handleUploadWithApiCustomRequestForAntDesign,
  progress: {
    strokeColor: {
      '0%': figmaColors.supportive1,
      '100%': figmaColors.supportive9,
    },
    strokeWidth: 3,
  },
};

const defaultPublicFileUploadOptions = {
  customRequest: handlePublicUploadWithApiCustomRequestForAntDesign,
  progress: {
    strokeColor: {
      '0%': figmaColors.supportive1,
      '100%': figmaColors.supportive9,
    },
    strokeWidth: 3,
  },
};

const defaultUploadOptions = {
  ...defaultFileUploadOptions,
  name: 'img-file',
  accept: ACCEPT_IMAGE_ONLY,
};

const defaultPublicUploadOptions = {
  ...defaultPublicFileUploadOptions,
  name: 'img-file',
  accept: ACCEPT_IMAGE_ONLY,
};

export const DefaultPrivateFileUpload = (props: FileUploadProps) => {
  return <Upload {...defaultFileUploadOptions} {...(props as any)} />;
};

export const DefaultPublicFileUpload = (props: FileUploadProps) => {
  return <Upload {...defaultPublicUploadOptions} {...(props as any)} />;
};

export const DefaultPrivateImageUpload = (props: FileUploadProps) => {
  return <Upload {...defaultUploadOptions} {...(props as any)} />;
};

export const DefaultPublicImageUploadWithoutDrag = (props: FileUploadProps) => {
  return <Upload {...defaultPublicUploadOptions} {...(props as any)} />;
};

export const DefaultPublicImageUpload = (props: FileUploadProps) => {
  return <Upload.Dragger {...defaultPublicUploadOptions} {...(props as any)} />;
};

export const DefaultPrivateImageUploadDragger = (props: FileUploadProps) => {
  return <Upload.Dragger {...defaultUploadOptions} {...(props as any)} />;
};

const Main = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  box-sizing: border-box;
  border-radius: 8px;
  padding-left: 16px;
  padding-right: 16px;
`;

export const FileUpload = (props: FileUploadProps) => {
  const { fileInfo, labelBtn, renderActionButton, fileName, fileExt, required } = props;

  const renderSubtitle = React.useCallback(() => {
    if (fileInfo && fileInfo.size) {
      return (
        <span>
          Uploaded : {moment(fileInfo?.uploadedDate).format(DATE_FORMAT_2)} | File size :{' '}
          {Math.round(fileInfo.size / 1024)}Kb
        </span>
      );
    }
    return (
      <span>
        {'Drag and Drop or '}
        <span className="em">Browse</span>
        {' to upload (max 100Kb)'}
      </span>
    );
  }, [fileInfo]);

  const renderIcon = React.useCallback(() => {
    const fileExtLowerCase = fileExt ? fileExt.toLowerCase() : '';
    switch (fileExtLowerCase) {
      case 'jpeg':
      case 'jpg':
      case 'png': {
        return <IconFileImage />;
      }
      default:
        return <IconUploadFile />;
    }
  }, [fileExt]);

  return (
    <DefaultPrivateImageUploadDragger
      style={required ? { borderColor: colorsV2.red100 } : {}}
      {...props}
    >
      <Main>
        <>
          <FileSelected>
            <div className="left-section">
              <div className="icon-wrapper">{renderIcon()}</div>
            </div>
            <div className="content-section">
              <div className="title">{fileName || 'Upload your files'}</div>
              {renderSubtitle()}
            </div>
          </FileSelected>
          {renderActionButton
            ? renderActionButton()
            : labelBtn && (
                <ButtonWrapper>
                  <Button size="small" type="2nd-supportive">
                    <ReadingText>{labelBtn}</ReadingText>
                  </Button>
                </ButtonWrapper>
              )}
        </>
      </Main>
    </DefaultPrivateImageUploadDragger>
  );
};
